import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Divider,
    TextField
} from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../../firebase";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where
} from "firebase/firestore";
import Menu from "../../panel/Menu";

function Roomtypewiseonline() {

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
    });

    const printRef = useRef();

    const [propertyId, setPropertyId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [bookingData, setBookingData] = useState([]);

    /* 🔐 AUTH + PROPERTY */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            } else {
                Toast.fire({ icon: "error", title: "Property not assigned" });
            }
        });

        return () => unsub();
    }, []);

    /* 📊 GET REPORT */
    const getReport = async () => {
        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: "Select From & To Date" });
            return;
        }

        if (!propertyId) {
            Toast.fire({ icon: "error", title: "Property not assigned" });
            return;
        }

        setLoading(false);
        setBookingData([]);

        try {
            /* ROOM TYPES */
            const roomTypeSnap = await getDocs(
                query(
                    collection(db, "rooms"),
                    where("propertyId", "==", propertyId)
                )
            );

            const roomTypes = roomTypeSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            if (!roomTypes.length) {
                Toast.fire({ icon: "info", title: "No Room Types Found" });
                return;
            }

            const rTypeIds = roomTypes.map(r => r.id).slice(0, 30);

            /* WEBSITE BOOKINGS */
            const bookingSnap = await getDocs(
                query(
                    collection(db, "websitebookings"),
                    where("propertyId", "==", propertyId),
                    where("rtypeid", "in", rTypeIds),
                    where("status", "==", "Paid"),
                    where("checkindate", ">=", fromDate),
                    where("checkindate", "<=", toDate)
                )
            );

            const bookings = bookingSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            /* GROUP BY ROOM TYPE */
            const finalData = roomTypes.map(rt => ({
                roomtype: rt.roomtype,
                bookings: bookings.filter(b => b.rtypeid === rt.id)
            }));

            setBookingData(finalData);
            setLoading(true);

        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Error loading report" });
        }
    };

    /* 🖨 PRINT */
    const handlePrint = () => {
        window.print();
    };

    return (
        <>
            <Menu />

            <div className="content-wrapper p-3"  >

                <Typography variant="h6" gutterBottom>
                    Room Type Wise Website Bookings
                </Typography>

                {/* FILTER */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-3" md={4}>
                                <TextField
                                    type="date"
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    fullWidth size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid className="col-sm-3" md={4}>
                                <TextField
                                    type="date"
                                    label="To Date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    fullWidth size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid className="col-sm-3" md={4} alignSelf="center">
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={getReport}
                                >
                                    Get Report
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* REPORT */}
                {loading && (
                    <>
                        <Card className="print-area" ref={printRef}>
                            <CardContent>

                                <Box textAlign="center" mb={2}>
                                    <Typography fontWeight="bold">
                                        From {moment(fromDate).format("DD/MM/YYYY")}
                                        {" "}to{" "}
                                        {moment(toDate).format("DD/MM/YYYY")}
                                    </Typography>
                                </Box>

                                {bookingData.map((rt, idx) => (
                                    <Box key={idx} mb={4}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{ background: "#e3f2fd", p: 1 }}
                                        >
                                            {rt.roomtype}
                                        </Typography>

                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Sr</TableCell>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Mobile</TableCell>
                                                        <TableCell>Checkin</TableCell>
                                                        <TableCell>Checkout</TableCell>
                                                        <TableCell>Rooms</TableCell>
                                                        <TableCell>Adults</TableCell>
                                                        <TableCell>Plan</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                        <TableCell align="right">Discount</TableCell>
                                                        <TableCell align="right">Paid</TableCell>
                                                        <TableCell align="right">Remaining</TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {rt.bookings.map((b, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{i + 1}</TableCell>
                                                            <TableCell>{b.name}</TableCell>
                                                            <TableCell>{b.phone}</TableCell>
                                                            <TableCell>{moment(b.checkindate).format("DD/MM/YYYY")}</TableCell>
                                                            <TableCell>{moment(b.checkoutdate).format("DD/MM/YYYY")}</TableCell>
                                                            <TableCell>{b.noofroom}</TableCell>
                                                            <TableCell>{b.adult}</TableCell>
                                                            <TableCell>{b.roomtype} / {b.planname}</TableCell>
                                                            <TableCell align="right">{b.total}</TableCell>
                                                            <TableCell align="right">{b.discountamount}</TableCell>
                                                            <TableCell align="right">{b.advance}</TableCell>
                                                            <TableCell align="right">{b.remaining}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Divider sx={{ mt: 2 }} />
                                    </Box>
                                ))}

                            </CardContent>
                        </Card>

                        <Box textAlign="center" mt={3}>
                            <Button variant="contained" color="success" onClick={handlePrint}>
                                Print Report
                            </Button>
                        </Box>
                    </>
                )}
            </div>
        </>
    );
}

export default Roomtypewiseonline;
